import React, { FC } from 'react'

import { WorkspaceLogo } from '@sketch/components'

import {
  Container,
  WorkspaceName,
  StyledLink,
  StyledTextH1,
  BlueCheckmark,
  StyledTextRaw,
} from './ProfileAvatar.styles'

import { PublicWorkspaceFragment } from '@sketch/gql-types'

export interface ProfileAvatarProps {
  workspaceName: string
  avatar?: PublicWorkspaceFragment['avatar']
  className?: string
  to?: string
  isSmall?: boolean
  isOfficial: boolean
}

type TextProps = Pick<ProfileAvatarProps, 'isSmall'>
type ProfileAvatarContentProps = OmitSafe<ProfileAvatarProps, 'to'>

const Text: React.FC<TextProps> = ({ isSmall, children }) =>
  isSmall ? (
    <StyledTextRaw textStyle="prominent.primary.E">{children}</StyledTextRaw>
  ) : (
    <StyledTextH1 textStyle="header.primary.I">{children}</StyledTextH1>
  )

const ProfileAvatarContent: React.FC<ProfileAvatarContentProps> = ({
  avatar,
  className,
  isSmall,
  workspaceName,
  isOfficial,
}) => {
  const size = isSmall ? '24px' : '88px'
  const src = avatar?.large ?? avatar?.small
  const blueCheckmarkSize = isSmall ? '16' : '22'

  return (
    <Container className={className} $isSmall={isSmall}>
      <WorkspaceLogo size={size} workspaceName={workspaceName} src={src} />
      <WorkspaceName>
        <Text isSmall={isSmall}>
          {workspaceName}
          {isOfficial && (
            <BlueCheckmark
              title="Official profile"
              width={blueCheckmarkSize}
              height={blueCheckmarkSize}
            />
          )}
        </Text>
      </WorkspaceName>
    </Container>
  )
}

export const ProfileAvatar: FC<ProfileAvatarProps> = ({
  workspaceName,
  avatar,
  className,
  isSmall,
  to,
  isOfficial = false,
}) => {
  const content = (
    <ProfileAvatarContent
      workspaceName={workspaceName}
      avatar={avatar}
      className={className}
      isSmall={isSmall}
      isOfficial={isOfficial}
    />
  )

  if (to) {
    return <StyledLink to={to}>{content}</StyledLink>
  }

  return content
}
